import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';

import { SharedModule } from '../../_shared/shared.module';
import { AlertService } from '../../_shared/services/alert.service';
import { UtilService } from '../../_shared/services/util.service';
import { GoogleMapService } from '../../_shared/services/google-map.service';

import { Location } from '../shared/location.model';
import { LocationModalService } from '../shared/location-modal.service';
import { LocationService } from '../shared/location.service';

import { CountryService } from '../../countries/shared/country.service';
import { CategoryService } from '../../categories/shared/category.service';
import { CategoryModalService } from '../../categories/shared/category-modal.service';

import EDITOR_TOOLBAR from '../../_constants/editor-toolbar';
import { NgxEditorModule, Editor, Toolbar } from 'ngx-editor';

@Component({
  selector: 'app-location-detail',
  templateUrl: './location-detail.component.html',
  standalone: true,
  imports: [SharedModule, NgxEditorModule],
})
export class LocationDetailComponent implements OnInit, OnDestroy {
  @Input() location: Location;
  @Input() mode: string;
  @Input() defaultLanguage;
  @Input() additionalLanguages;
  @Input() isModal = false;

  @Output() private savedLocation = new EventEmitter<Location>();
  @Output() private canceledLocation = new EventEmitter();

  form: FormGroup;
  formSubmited = false;

  // countryCtrl = new FormControl();
  // filteredCountries: Observable<any[]>;

  editor: Editor;
  toolbar: Toolbar = EDITOR_TOOLBAR.toolbarOptions;

  createMode = false;
  editMode = false;
  viewMode = false;

  labelAddLogo = '';

  urlEmbedVideo = '';

  groupedLocations = [];
  filteredGroupedLocations = [];
  loadingLocations = false;

  countries = [];
  loadingCountries = false;

  categories = [];
  loadingCategories = false;

  serverErrors = [];
  sendingForm = false;

  googleMapLoaded = false;

  private locations = [];
  private locationCopy = null;
  private photo = null;

  constructor(
    private locationService: LocationService,
    private locationModalService: LocationModalService,
    private countryService: CountryService,
    private categoryService: CategoryService,
    private categoryModalService: CategoryModalService,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private utilService: UtilService,
    private googleMapService: GoogleMapService
  ) {
    this.googleMapService.loadGoogleMapScript().then(() => {
      this.googleMapLoaded = true;
    });
  }

  ngOnInit() {
    this.editor = new Editor();

    this.setLocationForm();

    if (this.location.id) {
      this.setItemOptions(this.location, this.mode);

      this.form.patchValue(this.location);
    } else {
      this.createMode = true;
    }

    this.getLocations();
    this.getCountries();
    this.getCategories();

    // Listen to country changes to revalidate parent location
    this.form.get('country_id')?.valueChanges.subscribe(() => {
      this.filterLocationsByCountry();
      
      // Clear parent location if it doesn't match the new country
      const parentLocationId = this.form.get('parentLocation_id')?.value;
      const countryId = this.form.get('country_id')?.value;
      
      if (parentLocationId && countryId) {
        const parentLocation = this.locations.find((loc) => loc.id === parentLocationId || loc.name === parentLocationId);
        if (parentLocation && parentLocation.country_id !== countryId) {
          this.form.patchValue({ parentLocation_id: null });
        }
      }
      
      this.form.updateValueAndValidity();
    });

    // Listen to parent location changes to revalidate
    this.form.get('parentLocation_id')?.valueChanges.subscribe(() => {
      this.form.updateValueAndValidity();
    });
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  generateUrl() {
    const name = this.form.controls['name'].value;
    const newUrl = this.utilService.generateURL(name); // , this.editMode

    // const name = event.target.value;
    // console.log('event', event);
    // console.log('name', name);
    // console.log('newUrl', newUrl);

    this.form.controls['url'].setValue(newUrl);
  }

  // Referenced from app-translation-options
  onOpenedTranslation(language: any) {
    this.locationModalService.openLocationTranslation(this.location, language);
  }

  openCategoriesModal() {
    const successCallback = () => {
      this.getCategories();
    };

    this.categoryModalService.openCategoriesModal(successCallback);
  }

  openLogo() {
    const successCallback = ({ file, croppedImage }) => {
      if (this.editMode) {
        this.locationService.updatePickupLogo(this.location.id, file).subscribe((response) => {
          this.alertService.open('PHOTO_UPDATED');

          this.location.logoUrl = this.utilService.cleanItemCache(response, 'logo');
        });
      } else {
        this.photo = file;

        this.location.logoUrl = croppedImage;
      }
    };

    this.utilService.openLogo(successCallback);
  }

  // Referenced from app-map
  onSelectedMapPlace({ lat, lng, zoom }) {
    this.form.controls['geo_lat'].setValue(lat);
    this.form.controls['geo_lng'].setValue(lng);
    this.form.controls['geo_zoom'].setValue(zoom);
  }

  setSuperLocation() {
    if (this.form.controls['is_superior_location'].value) {
      this.form.controls['parentLocation_id'].setValue(null);
      this.form.controls['parentLocation_id'].disable();
      this.form.controls['parentLocation_id'].setValidators(null);

      // this.form.controls['parentLocation_id'].clearValidators();
      // this.form.controls['parentLocation_id'].setErrors(null);
    } else {
      const value = this.editMode ? this.locationCopy.parentLocation_id : null;
      this.form.controls['parentLocation_id'].setValue(value);
      this.form.controls['parentLocation_id'].enable();
      this.form.controls['parentLocation_id'].setValidators(Validators.required); // [Validators.required]
    }

    this.form.updateValueAndValidity();
  }

  // Referenced from app-item-detail-form-actions
  onSavedLocation() {
    this.formSubmited = true;

    /*console.log('Errors');
    console.log(this.form.invalid);

    Object.keys(this.form.controls).forEach((key) => {
      const controlErrors: ValidationErrors = this.form.get(key).errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach((keyError) => {
          console.log(
            'Key control: ' + key + ', keyError: ' + keyError + ', err value: ',
            controlErrors[keyError]
          );
        });
      }
    });*/

    if (this.form.invalid || this.categories.length === 0) {
      // || this.locations.length === 0
      this.serverErrors = [];

      return;
    }

    const formData = this.form.value;

    this.sendingForm = true;

    // console.log(this.location);
    // console.log(formData);

    // The location is not super location and belongs to a super location
    if (!formData.is_superior_location) {
      const parentLocation = this.locations.find(
        (item) => item.name === formData.parentLocation_id
      );

      // parent location found if format is parentLocation_id: "5e96538353321a0c06f97592"
      if (parentLocation) {
        formData.parentLocation_id = parentLocation.id;
      }
    }

    // console.log(formData);
    // return;

    this.locationService
      .saveLocation(this.location.id, formData, this.defaultLanguage.lang, this.photo)
      .subscribe({
        next: (response) => {
          let msg = '';

          if (this.editMode) {
            msg = 'LOCATION_UPDATED';

            // needed if we want to keep the page opened or if used in page
            this.location = response;
            this.setItemOptions(response);
          } else {
            msg = 'LOCATION_CREATED';
            // this.location = response; // reload item - needed ?

            // if (!this.isModal) {
            // this.savedLocation.emit(response);
            // }
          }

          this.alertService.open(msg);

          this.sendingForm = false;

          // if (this.isModal) {
          this.savedLocation.emit(response);
          // }
        },
        error: (response) => {
          this.serverErrors = this.utilService.setFormServerErrors(response.error.errors);

          this.utilService.scrollToTop();

          this.sendingForm = false;
        },
      });
  }

  // Referenced from app-item-detail-form-actions
  onCanceled() {
    if (this.editMode) {
      this.cancelEdit();
      return;
    }

    this.canceledLocation.emit();
  }

  // Referenced from app-item-detail-form-actions
  onEditedLocation() {
    this.editMode = true;
    this.viewMode = false;

    this.setSuperLocation();
  }

  displayCountry(country): string {
    return country && country.name ? country.name : '';
  }

  compareLocations = (item, selected) => {
    if (!this.editMode) return false;

    // Parents locations
    if (!item.id) {
      return this.location.parentLocation && item.parent === this.location.parentLocation.name;
    } else {
      return selected === item.id;
    }

    // return false;
  };

  private cancelEdit() {
    this.location = this.locationCopy;

    this.setItemOptions(this.location);
  }

  private setItemOptions(location, mode = '') {
    this.createMode = false;
    this.editMode = false;
    this.viewMode = true;

    if (mode === 'edit') {
      this.onEditedLocation();
    }

    this.urlEmbedVideo = this.utilService.embedYouTubeVideo(location.url_video);

    this.labelAddLogo = this.location && this.location.logo.url ? 'CHANGE_PHOTO' : 'ADD_PHOTO';

    this.locationCopy = { ...location };
  }

  private setLocationForm() {
    this.form = this.formBuilder.group(
      {
        name: [
          '',
          {
            validators: [
              Validators.required,
              // Validators.pattern(this.utilService.selectInputPattern('text')),
            ],
            updateOn: 'blur',
          },
        ],
        url: ['', Validators.required], // this.location.url,
        short_description: '',
        long_description: '',
        highlights: '',
        url_video: '',
        geo_lat: '',
        geo_lng: '',
        geo_zoom: '',

        category_ids: ['', Validators.required],
        country_id: [null, Validators.required],
        parentLocation_id: [null, Validators.required], // '' test null ()
        is_superior_location: '', //['']
      },
      {
        validators: this.validateParentLocationCountry.bind(this),
      }
    );
  }

  private getLocations() {
    const params = {
      get: 'active',
      skip_check_role: true,
      lang: this.defaultLanguage.lang,
    };

    this.loadingLocations = true;

    this.locationService.getLocations(params).subscribe((response) => {
      this.locations = [...response];

      // console.log(this.locations);

      this.groupedLocations = this.locationService.groupLocations(this.locations); // , '', 'locations'

      // console.log(this.groupedLocations);

      this.filterLocationsByCountry();

      this.loadingLocations = false;
    });
  }

  private getCountries() {
    const params = {
      get: 'active',
      skip_check_role: true,
      lang: this.defaultLanguage.lang,
    };

    this.loadingCountries = true;

    this.countryService.getCountries(params).subscribe((response) => {
      this.countries = response;

      /*const countrySelected = this.countries.filter(
        (country) => country.id === this.location.country_id
      );

      this.countryCtrl.setValue(countrySelected);
      console.log(countrySelected);
      */

      this.loadingCountries = false;
    });
  }

  // private filterCountries(value: string) {
  //   const filterValue = value.toLowerCase();
  //   return this.countries.filter((country) => country.name.toLowerCase().includes(filterValue));
  // }

  private getCategories() {
    const params = {
      get: 'location',
      skip_check_role: true,
      lang: this.defaultLanguage.lang,
    };

    this.loadingCategories = true;

    this.categoryService.getCategories(params).subscribe((response) => {
      this.categories = response;

      this.loadingCategories = false;
    });
  }

  private filterLocationsByCountry() {
    const selectedCountryId = this.form.get('country_id')?.value;

    if (!selectedCountryId) {
      // If no country selected, show all locations
      this.filteredGroupedLocations = [...this.groupedLocations];
      return;
    }

    // Filter locations array by country
    const filteredLocations = this.locations.filter(
      (location) => location.country_id === selectedCountryId
    );

    // Regroup filtered locations
    this.filteredGroupedLocations = this.locationService.groupLocations(filteredLocations);
  }

  private validateParentLocationCountry(formGroup: FormGroup): ValidationErrors | null {
    const isSuperiorLocation = formGroup.get('is_superior_location')?.value;
    const parentLocationId = formGroup.get('parentLocation_id')?.value;
    const countryId = formGroup.get('country_id')?.value;

    if (isSuperiorLocation || !parentLocationId || !countryId) {
      return null;
    }

    // parentLocationId can be either ID (regular locations) or name (superior locations)
    let parentLocation = this.locations.find((loc) => loc.id === parentLocationId);

    if (!parentLocation) {
      parentLocation = this.locations.find((loc) => loc.name === parentLocationId);
    }

    if (!parentLocation) {
      return null;
    }

    if (parentLocation.country_id !== countryId) {
      return { parentLocationCountryMismatch: true };
    }

    return null;
  }
}

/*

    // this.filteredCountries = this.countryCtrl.valueChanges.pipe(
    //   startWith(''),
    //   map((value) => {
    //     const name = typeof value === 'string' ? value : value?.name;
    //     return name ? this.filterCountries(name as string) : this.countries.slice();
    //   })
    // );

// import { MatSelectSearchModule } from 'mat-select-search';
// import { AngularMultiSelectModule } from 'angular2-multiselect-dropdown';

  // filteredCountries = [];

    settings = {};
  selectedItems = [];
  countrySelected = [];


  /*   onKey(event) {
    this.filteredCountries = [];

    const value = event.target.value;

    this.searchCountry(value);
    // this.filteredCountries = this.searchCountry(value);
  }

  searchCountry(value: string) {
    // let filter = value.toLowerCase();
    // return this.countries.filter((country) => country.name.toLowerCase().includes(filter));

    let filter = value.toLowerCase();
    for (let i = 0; i < this.countries.length; i++) {
      let option = this.countries[i];
      if (option.name.toLowerCase().indexOf(filter) >= 0) {
        this.filteredCountries.push(option);
      }
    }
  } 


      // listen for search field value changes
    // this.bankFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
    //   this.getCountries();
    // });

    // this.settings = {
    //   // text: 'Select Skills',
    //   // classes: 'form-control',
    //   labelKey: 'name',
    //   enableSearchFilter: true,
    //   singleSelection: true,
    // };


          // this.filteredCountries = [...this.countries];

      // this.countrySelected = this.countries.find(
      //   (country) => country.id === this.location.country_id
      // );

      // this.countrySelected = this.countries.filter(
      //   (country) => country.id === this.location.country_id
      // );

      // console.log(this.countries);




const fields = [
  // 'name',
  'short_description',
  'long_description',
  'highlights',
  'url_video',
  'geo_lat',
  'geo_lng',
  'geo_zoom',
  'is_superior_location',
];

const formData = this.utilService.deleteNullFields(fields, this.form.value); // We do not need this as we use patchValue

  this.form = this.formBuilder.group({
      name: [
        this.location.name,
        {
          validators: [
            Validators.required,
            // Validators.pattern(this.utilService.selectInputPattern('text')),
          ],
          updateOn: 'blur',
        },
      ],
      url: [this.location.url, Validators.required],
      short_description: [this.location.short_description],
      long_description: [this.location.long_description],
      highlights: [this.location.highlights],
      url_video: [this.location.url_video],
      geo_lat: [this.location.geo_lat],
      geo_lng: [this.location.geo_lng],
      geo_zoom: [this.location.geo_zoom],

      category_ids: [this.location.category_ids, Validators.required],
      country_id: [this.location.country_id, Validators.required],
      parentLocation_id: [this.location.parentLocation_id, Validators.required],
      is_superior_location: [this.location.is_superior_location],
    }); */

// if (this.location.id) {
//   if (this.editMode && !fields.some((field) => formData[field] !== this.locationCopy[field])) {
//     this.onCanceled();
//     return;
//   }
// }
